// src/components/SettingsModal.jsx
import { useState, useCallback, useEffect, useRef } from "react";
import { X, Eye, EyeOff, Mail, Lock, User, Check, AlertCircle, Sword, ExternalLink, Package, Truck, RotateCcw, ChevronDown, ChevronUp, Camera, ZoomIn, ZoomOut, MailCheck, ShieldAlert } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { API } from "../utils/constants";
import { OriginalModalShell } from "./ModalShell";
import { ROLE_LABELS } from "../utils/permissions";

/* ── Style constants — identical to AuthModal ───────────────────────────── */
const INPUT_BASE = {
  width: "100%", background: "rgba(255,255,255,.04)",
  border: "1px solid rgba(123,94,167,.25)",
  padding: "10px 40px 10px 36px", fontSize: 13, color: "#e2d9f3",
  fontFamily: "'Inter',sans-serif", outline: "none",
  transition: ".15s", boxSizing: "border-box",
};
const LABEL_STYLE = {
  display: "block", fontSize: 11, fontWeight: 600,
  color: "#9b8dbe", letterSpacing: .5, textTransform: "uppercase",
  marginBottom: 5, fontFamily: "'Cinzel',serif",
};
const ICON_STYLE = {
  position: "absolute", left: 11, top: "50%", transform: "translateY(-50%)",
  color: "#4a4060", pointerEvents: "none", display: "flex", alignItems: "center", zIndex: 1,
};
const fo  = e => e.target.style.borderColor = "rgba(123,94,167,.6)";
const fob = e => e.target.style.borderColor = "rgba(123,94,167,.25)";

const DISCORD_ICON = (
  <svg width="17" height="17" viewBox="0 0 127.14 96.36" fill="white">
    <path d="M107.7,8.07A105.15,105.15,0,0,0,81.47,0a72.06,72.06,0,0,0-3.36,6.83A97.68,97.68,0,0,0,49,6.83,72.37,72.37,0,0,0,45.64,0,105.89,105.89,0,0,0,19.39,8.09C2.79,32.65-1.71,56.6.54,80.21h0A105.73,105.73,0,0,0,32.71,96.36,77.7,77.7,0,0,0,39.6,85.25a68.42,68.42,0,0,1-10.85-5.18c.91-.66,1.8-1.34,2.66-2a75.57,75.57,0,0,0,64.32,0c.87.71,1.76,1.39,2.66,2a68.68,68.68,0,0,1-10.87,5.19,77,77,0,0,0,6.89,11.1A105.25,105.25,0,0,0,126.6,80.22h0C129.24,52.84,122.09,29.11,107.7,8.07ZM42.45,65.69C36.18,65.69,31,60,31,53s5-12.74,11.43-12.74S54,46,53.89,53,48.84,65.69,42.45,65.69Zm42.24,0C78.41,65.69,73.25,60,73.25,53s5-12.74,11.44-12.74S96.23,46,96.12,53,91.08,65.69,84.69,65.69Z"/>
  </svg>
);

/* ── Shared helpers ──────────────────────────────────────────────────────── */
function FieldWrap({ label, children }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <label style={LABEL_STYLE}>{label}</label>
      <div style={{ position: "relative", display: "flex", alignItems: "center" }}>{children}</div>
    </div>
  );
}
function EyeBtn({ show, onToggle }) {
  return (
    <button type="button" onClick={onToggle} style={{
      position: "absolute", right: 0, top: 0, bottom: 0, width: 38,
      background: "none", border: "none", borderLeft: "1px solid rgba(123,94,167,.2)",
      color: "#4a4060", cursor: "pointer",
      display: "flex", alignItems: "center", justifyContent: "center", transition: ".15s", zIndex: 2,
    }}
      onMouseOver={e => e.currentTarget.style.color = "#9b7fd4"}
      onMouseOut={e =>  e.currentTarget.style.color = "#4a4060"}
    >
      {show ? <EyeOff style={{ width: 14, height: 14 }} /> : <Eye style={{ width: 14, height: 14 }} />}
    </button>
  );
}
function SaveBtn({ onClick, loading, label = "Save Changes", disabled = false }) {
  return (
    <button onClick={onClick} disabled={loading || disabled} style={{
      padding: "10px 22px", borderRadius: 8,
      background: "linear-gradient(135deg,#7b5ea7,#5a3d8a)",
      border: "none", color: "#fff", fontFamily: "'Cinzel',serif",
      fontSize: 12, fontWeight: 700, letterSpacing: 1, textTransform: "uppercase",
      cursor: loading ? "not-allowed" : "pointer", transition: ".15s", marginTop: 4,
      boxShadow: "0 0 20px rgba(123,94,167,.3)", opacity: loading ? .6 : 1,
    }}
      onMouseOver={e => { if (!loading) { e.currentTarget.style.boxShadow = "0 0 30px rgba(123,94,167,.5)"; e.currentTarget.style.transform = "translateY(-1px)"; }}}
      onMouseOut={e =>  { e.currentTarget.style.boxShadow = "0 0 20px rgba(123,94,167,.3)"; e.currentTarget.style.transform = ""; }}
    >
      {loading ? "Saving…" : label}
    </button>
  );
}
function Feedback({ saved, error }) {
  if (!saved && !error) return null;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 10, fontSize: 12, fontFamily: "'Cinzel',serif", color: saved ? "#4ade80" : "#f87171" }}>
      {saved ? <Check style={{ width: 13, height: 13 }} /> : <AlertCircle style={{ width: 13, height: 13 }} />}
      {saved ? "Saved successfully" : error}
    </div>
  );
}
function SectionDivider({ label }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, margin: "20px 0" }}>
      <div style={{ flex: 1, height: 1, background: "#2a2040" }} />
      {label && <span style={{ fontSize: 11, color: "#4a4060", fontFamily: "'Cinzel',serif", letterSpacing: .5, whiteSpace: "nowrap" }}>{label}</span>}
      <div style={{ flex: 1, height: 1, background: "#2a2040" }} />
    </div>
  );
}

function ProfilePictureCropper({ src, onCancel, onSave }) {
  const VIEW = 300;
  const OUT = 512;
  const imgRef = useRef(null);
  const dragRef = useRef(null);
  const [meta, setMeta] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });

  const baseScale = meta ? Math.max(VIEW / meta.w, VIEW / meta.h) : 1;
  const renderedW = meta ? meta.w * baseScale * zoom : VIEW;
  const renderedH = meta ? meta.h * baseScale * zoom : VIEW;

  const clampOffset = useCallback((next, nextZoom = zoom) => {
    if (!meta) return next;
    const scale = Math.max(VIEW / meta.w, VIEW / meta.h);
    const w = meta.w * scale * nextZoom;
    const h = meta.h * scale * nextZoom;
    const maxX = Math.max(0, (w - VIEW) / 2);
    const maxY = Math.max(0, (h - VIEW) / 2);
    return {
      x: Math.max(-maxX, Math.min(maxX, next.x)),
      y: Math.max(-maxY, Math.min(maxY, next.y)),
    };
  }, [meta, zoom]);

  const changeZoom = (value) => {
    const z = Math.max(1, Math.min(3, Number(value)));
    setZoom(z);
    setOffset(current => clampOffset(current, z));
  };

  const pointerDown = (e) => {
    e.currentTarget.setPointerCapture?.(e.pointerId);
    dragRef.current = { x: e.clientX, y: e.clientY, ox: offset.x, oy: offset.y };
  };
  const pointerMove = (e) => {
    const d = dragRef.current;
    if (!d) return;
    setOffset(clampOffset({ x: d.ox + e.clientX - d.x, y: d.oy + e.clientY - d.y }));
  };
  const pointerUp = () => { dragRef.current = null; };

  const save = () => {
    const img = imgRef.current;
    if (!img || !meta) return;
    const canvas = document.createElement("canvas");
    canvas.width = OUT; canvas.height = OUT;
    const ctx = canvas.getContext("2d");
    const ratio = OUT / VIEW;
    const left = VIEW / 2 + offset.x - renderedW / 2;
    const top = VIEW / 2 + offset.y - renderedH / 2;
    ctx.fillStyle = "#000";
    ctx.fillRect(0, 0, OUT, OUT);
    ctx.drawImage(img, left * ratio, top * ratio, renderedW * ratio, renderedH * ratio);
    onSave(canvas.toDataURL("image/jpeg", 0.9));
  };

  return (
    <div className="toa-overlay" style={{ zIndex: 650 }} onClick={e => e.target === e.currentTarget && onCancel()}>
      <div className="toa-modal" style={{ maxWidth: 430, textAlign: "center" }}>
        <button className="toa-modal-close" onClick={onCancel}><X style={{ width: 14, height: 14 }} /></button>
        <div className="toa-modal-title" style={{ fontSize: 18, marginBottom: 5 }}>Adjust Profile Picture</div>
        <div className="toa-modal-sub" style={{ marginBottom: 18 }}>Drag to reposition and use the slider to zoom.</div>

        <div
          onPointerDown={pointerDown}
          onPointerMove={pointerMove}
          onPointerUp={pointerUp}
          onPointerCancel={pointerUp}
          style={{
            width: VIEW, height: VIEW, maxWidth: "100%", aspectRatio: "1 / 1", margin: "0 auto",
            position: "relative", overflow: "hidden", background: "#07070b", cursor: "grab", touchAction: "none", userSelect: "none",
          }}
        >
          <img
            ref={imgRef}
            src={src}
            alt="Crop preview"
            draggable={false}
            onLoad={e => {
              const n = e.currentTarget;
              setMeta({ w: n.naturalWidth, h: n.naturalHeight });
              setZoom(1); setOffset({ x: 0, y: 0 });
            }}
            style={{
              position: "absolute", left: "50%", top: "50%",
              width: renderedW, height: renderedH, maxWidth: "none",
              transform: `translate(-50%, -50%) translate(${offset.x}px, ${offset.y}px)`,
              pointerEvents: "none",
            }}
          />
          <div style={{
            position: "absolute", inset: 2, borderRadius: "50%", border: "2px solid rgba(255,255,255,.95)",
            boxShadow: "0 0 0 999px rgba(0,0,0,.58)", pointerEvents: "none",
          }} />
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 18 }}>
          <ZoomOut style={{ width: 15, height: 15, color: "var(--muted)" }} />
          <input type="range" min="1" max="3" step="0.01" value={zoom} onChange={e => changeZoom(e.target.value)} style={{ flex: 1, accentColor: "var(--purpleB)" }} />
          <ZoomIn style={{ width: 15, height: 15, color: "var(--muted)" }} />
        </div>

        <div style={{ display: "flex", gap: 9, marginTop: 18 }}>
          <button className="toa-btn toa-btn-ghost" style={{ flex: 1 }} onClick={onCancel}>Cancel</button>
          <button className="toa-btn toa-btn-p" style={{ flex: 1 }} onClick={save} disabled={!meta}>Save Picture</button>
        </div>
      </div>
    </div>
  );
}

/* ── Profile tab ──────────────────────────────────────────────────────────── */
function ProfileTab({ authUser, accessToken, doRefresh }) {
  const [username, setUsername] = useState(authUser?.username || "");
  const [verifyStatus, setVerifyStatus] = useState({ busy: false, error: '', message: '', retryAfter: 0 });
  const resendMyVerification = async () => {
    if (verifyStatus.busy || verifyStatus.retryAfter > 0) return;
    setVerifyStatus(v => ({ ...v, busy: true, error: '', message: '' }));
    try {
      const response = await fetch(`${API}/auth/resend-my-verification`, {
        method: 'POST', credentials: 'include',
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        setVerifyStatus({ busy: false, error: data.error || 'Unable to resend verification email.', message: '', retryAfter: data.retryAfter || 0 });
        return;
      }
      setVerifyStatus({ busy: false, error: '', message: 'If your email needs verification, a link has been sent.', retryAfter: 120 });
    } catch {
      setVerifyStatus({ busy: false, error: 'Network error. Please try again.', message: '', retryAfter: 0 });
    }
  };
  useEffect(() => {
    if (verifyStatus.retryAfter <= 0) return;
    const id = setInterval(() => setVerifyStatus(v => ({ ...v, retryAfter: Math.max(0, v.retryAfter - 1) })), 1000);
    return () => clearInterval(id);
  }, [verifyStatus.retryAfter > 0]);

  const [email,    setEmail]    = useState("");
  const [curPw,    setCurPw]    = useState("");
  const [newPw,    setNewPw]    = useState("");
  const [confPw,   setConfPw]   = useState("");
  const [showCur,  setShowCur]  = useState(false);
  const [showNew,  setShowNew]  = useState(false);
  const [showConf, setShowConf] = useState(false);
  const [profSt,   setProfSt]   = useState({ loading: false, saved: false, error: "" });
  const [picSt, setPicSt] = useState({ loading: false, saved: false, error: "" });
  const [cropSrc, setCropSrc] = useState(null);
  const pictureInputRef = useRef(null);
  const [pwSt,     setPwSt]     = useState({ loading: false, saved: false, error: "" });

  const saveProfile = async () => {
    setProfSt({ loading: true, saved: false, error: "" });
    try {
      const body = {};
      if (username.trim() && username.trim() !== authUser?.username) body.username = username.trim();
      if (email.trim()) body.email = email.trim();
      if (!Object.keys(body).length) { setProfSt({ loading: false, saved: false, error: "Nothing to change." }); return; }
      const res  = await fetch(`${API}/auth/profile`, {
        method: "PUT", credentials: "include",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${accessToken}` },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) { setProfSt({ loading: false, saved: false, error: data.errors?.[0]?.msg || data.error || "Failed." }); return; }
      await doRefresh?.();
      setEmail("");
      setProfSt({ loading: false, saved: true, error: "" });
      setTimeout(() => setProfSt(s => ({ ...s, saved: false })), 3000);
    } catch { setProfSt({ loading: false, saved: false, error: "Network error." }); }
  };

  const savePassword = async () => {
    setPwSt({ loading: true, saved: false, error: "" });
    if (newPw !== confPw) { setPwSt({ loading: false, saved: false, error: "Passwords don't match." }); return; }
    if (newPw.length < 8)  { setPwSt({ loading: false, saved: false, error: "Minimum 8 characters." }); return; }
    try {
      const res  = await fetch(`${API}/auth/password`, {
        method: "PUT", credentials: "include",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${accessToken}` },
        body: JSON.stringify({ currentPassword: curPw, newPassword: newPw }),
      });
      const data = await res.json();
      if (!res.ok) { setPwSt({ loading: false, saved: false, error: data.errors?.[0]?.msg || data.error || "Failed." }); return; }
      setCurPw(""); setNewPw(""); setConfPw("");
      setPwSt({ loading: false, saved: true, error: "" });
      setTimeout(() => setPwSt(s => ({ ...s, saved: false })), 3000);
    } catch { setPwSt({ loading: false, saved: false, error: "Network error." }); }
  };

  const updatePicture = async (profilePicture) => {
    setPicSt({ loading: true, saved: false, error: "" });
    try {
      const res = await fetch(`${API}/auth/profile-picture`, {
        method: "PUT", credentials: "include",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${accessToken}` },
        body: JSON.stringify({ profilePicture }),
      });
      const data = await res.json();
      if (!res.ok) { setPicSt({ loading: false, saved: false, error: data.error || "Could not update picture." }); return; }
      await doRefresh?.();
      setPicSt({ loading: false, saved: true, error: "" });
      setTimeout(() => setPicSt(s => ({ ...s, saved: false })), 3000);
    } catch { setPicSt({ loading: false, saved: false, error: "Network error." }); }
  };

  const choosePicture = (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    if (!["image/png", "image/jpeg", "image/webp"].includes(file.type)) {
      setPicSt({ loading: false, saved: false, error: "Use a PNG, JPG, or WebP image." }); return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setPicSt({ loading: false, saved: false, error: "Profile picture must be 5 MB or smaller." }); return;
    }
    const reader = new FileReader();
    reader.onload = () => setCropSrc(reader.result);
    reader.readAsDataURL(file);
  };

  const saveCroppedPicture = async (profilePicture) => {
    setCropSrc(null);
    await updatePicture(profilePicture);
  };

  return (
    <>
      {/* Identity pill */}
      <div style={{
        display: "flex", alignItems: "center", gap: 12,
        background: "rgba(123,94,167,.08)", border: "1px solid rgba(123,94,167,.2)",
        borderRadius: 8, padding: "11px 14px", marginBottom: 20,
      }}>
        <div style={{ flexShrink: 0 }}>
          <button type="button" onClick={() => pictureInputRef.current?.click()} title="Change profile picture" style={{
            width: 52, height: 52, borderRadius: "50%", background: "linear-gradient(135deg,#7b5ea7,#5a3d8a)",
            display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, overflow: "hidden",
            border: "1px solid rgba(155,127,212,.45)", padding: 0, cursor: "pointer", position: "relative",
          }}>
            {authUser?.profile_picture ? <img src={authUser.profile_picture} alt="Profile" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : <User style={{ width: 19, height: 19, color: "#fff" }} />}
            <span style={{ position: "absolute", inset: 0, display: "flex", alignItems: "flex-end", justifyContent: "center", paddingBottom: 4, background: "linear-gradient(transparent 48%, rgba(0,0,0,.68))", color: "#fff" }}>
              <Camera style={{ width: 13, height: 13 }} />
            </span>
          </button>
        </div>
        <div>
          <div style={{ fontFamily: "'Cinzel',serif", fontSize: 13, color: "#e2d9f3", fontWeight: 600 }}>{authUser?.username}</div>
          <div style={{ fontSize: 11, color: "#6b6080", marginTop: 1 }}>{ROLE_LABELS[authUser?.role] || authUser?.role}</div>
        </div>
        {picSt.saved && (
          <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 6, fontSize: 12, fontFamily: "'Cinzel',serif", color: "#4ade80", whiteSpace: "nowrap" }}>
            <Check style={{ width: 13, height: 13 }} />
            Saved successfully
          </div>
        )}
      </div>

      <div style={{ marginBottom: 18 }}>
        <input ref={pictureInputRef} type="file" accept="image/png,image/jpeg,image/webp" onChange={choosePicture} disabled={picSt.loading} style={{ display: "none" }} />
        <div style={{ fontSize: 10.5, color: "#6b6080" }}>Click your profile picture above to upload or change it. PNG, JPG or WebP · up to 5 MB.</div>
        {authUser?.profile_picture && (
          <button
            type="button"
            onClick={() => updatePicture(null)}
            disabled={picSt.loading}
            style={{
              marginTop: 7, padding: 0, border: "none", background: "transparent",
              color: "#f87171", fontSize: 11, fontFamily: "'Inter',sans-serif",
              cursor: picSt.loading ? "default" : "pointer", opacity: picSt.loading ? .5 : 1,
              textDecoration: "none",
            }}
          >
            Remove profile picture
          </button>
        )}
        <Feedback saved={false} error={picSt.error} />
      </div>

      <FieldWrap label="Username">
        <span style={ICON_STYLE}><User style={{ width: 14, height: 14 }} /></span>
        <input type="text" value={username} onChange={e => setUsername(e.target.value)}
          placeholder="Change your username" className="no-right-pad"
          style={INPUT_BASE} onFocus={fo} onBlur={fob} />
      </FieldWrap>
      <div style={{ background: 'rgba(123,94,167,.10)', border: '1px solid rgba(123,94,167,.30)', borderRadius: 10, padding: '14px', marginBottom: 17 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 5 }}>
          {authUser?.emailVerified ? <MailCheck size={19} color="#76d7a0" /> : <ShieldAlert size={19} color="#e6bb69" />}
          <span style={{ fontFamily: "'Cinzel',serif", color: '#e2d9f3', fontSize: 13, fontWeight: 700 }}>Email verification</span>
          <span style={{ marginLeft: 'auto', color: authUser?.emailVerified ? '#76d7a0' : '#e6bb69', fontSize: 11, fontWeight: 700 }}>
            {authUser?.emailVerified ? '✓ Verified' : 'Not verified'}
          </span>
        </div>
        <div style={{ fontSize: 12, color: '#a99bbf', lineHeight: 1.5 }}>
          {authUser?.emailVerified ? 'Your email address has been confirmed.' : 'Please verify your email address to activate your account.'}
        </div>
        {!authUser?.emailVerified && <>
          <button type="button" onClick={resendMyVerification} disabled={verifyStatus.busy || verifyStatus.retryAfter > 0}
            style={{ background: 'linear-gradient(135deg,#7b5ea7,#5a3d8a)', border: 0, borderRadius: 8, color: '#fff', padding: '10px 14px', marginTop: 12, fontFamily: "'Cinzel',serif", fontSize: 11, fontWeight: 700, cursor: 'pointer', opacity: verifyStatus.busy || verifyStatus.retryAfter > 0 ? .6 : 1 }}>
            {verifyStatus.busy ? 'Sending…' : verifyStatus.retryAfter > 0 ? `Resend in ${verifyStatus.retryAfter}s` : 'Resend verification email'}
          </button>
          {verifyStatus.error && <div role="alert" style={{ color: '#fca5a5', fontSize: 12, marginTop: 9 }}>{verifyStatus.error}</div>}
          {verifyStatus.message && <div role="status" style={{ color: '#86efac', fontSize: 12, marginTop: 9 }}>{verifyStatus.message}</div>}
        </>}
      </div>
      <FieldWrap label="Email Address">
        <span style={ICON_STYLE}><Mail style={{ width: 14, height: 14 }} /></span>
        <input type="email" value={email} onChange={e => setEmail(e.target.value)}
          placeholder="New email address" className="no-right-pad"
          style={INPUT_BASE} onFocus={fo} onBlur={fob} />
      </FieldWrap>
      <SaveBtn onClick={saveProfile} loading={profSt.loading} />
      <Feedback saved={profSt.saved} error={profSt.error} />

      <SectionDivider label="Change Password" />

      <FieldWrap label="Current Password">
        <span style={ICON_STYLE}><Lock style={{ width: 14, height: 14 }} /></span>
        <input type={showCur ? "text" : "password"} value={curPw} onChange={e => setCurPw(e.target.value)}
          placeholder="Current password" style={{ ...INPUT_BASE, paddingRight: 40 }} onFocus={fo} onBlur={fob} />
        <EyeBtn show={showCur} onToggle={() => setShowCur(p => !p)} />
      </FieldWrap>
      <FieldWrap label="New Password">
        <span style={ICON_STYLE}><Lock style={{ width: 14, height: 14 }} /></span>
        <input type={showNew ? "text" : "password"} value={newPw} onChange={e => setNewPw(e.target.value)}
          placeholder="Minimum 8 characters" style={{ ...INPUT_BASE, paddingRight: 40 }} onFocus={fo} onBlur={fob} />
        <EyeBtn show={showNew} onToggle={() => setShowNew(p => !p)} />
      </FieldWrap>
      <FieldWrap label="Confirm New Password">
        <span style={ICON_STYLE}><Lock style={{ width: 14, height: 14 }} /></span>
        <input type={showConf ? "text" : "password"} value={confPw} onChange={e => setConfPw(e.target.value)}
          placeholder="Repeat new password" style={{ ...INPUT_BASE, paddingRight: 40 }} onFocus={fo} onBlur={fob} />
        <EyeBtn show={showConf} onToggle={() => setShowConf(p => !p)} />
      </FieldWrap>
      <SaveBtn onClick={savePassword} loading={pwSt.loading} label="Update Password" />
      <Feedback saved={pwSt.saved} error={pwSt.error} />

      {cropSrc && (
        <ProfilePictureCropper
          src={cropSrc}
          onCancel={() => setCropSrc(null)}
          onSave={saveCroppedPicture}
        />
      )}
    </>
  );
}

/* ── Connections tab ──────────────────────────────────────────────────────── */
function ConnectionsTab({ authUser, accessToken }) {
  const { refreshProfile } = useAuth();
  const [mcName,    setMcName]    = useState(authUser?.minecraft_username || "");
  const [searchSt,  setSearchSt]  = useState("idle"); // idle | searching | found | notfound | error
  const [foundName, setFoundName] = useState(null);
  const [mcSt,      setMcSt]      = useState({ loading: false, saved: false, error: "" });

  const searchPlayer = useCallback(async () => {
    const q = mcName.trim();
    if (!q || q.length < 3) return;
    setSearchSt("searching"); setFoundName(null); setMcSt({ loading: false, saved: false, error: "" });
    try {
      const r    = await fetch(`${API}/tracker/verify-mc?username=${encodeURIComponent(q)}`);
      const data = await r.json();
      if (data.exists) { setFoundName(data.playerName); setSearchSt("found"); }
      else             { setSearchSt("notfound"); }
    } catch { setSearchSt("error"); setMcSt({ loading: false, saved: false, error: "Network error." }); }
  }, [mcName]);

  const saveMc = async () => {
    if (!foundName) return;
    setMcSt({ loading: true, saved: false, error: "" });
    try {
      const res  = await fetch(`${API}/auth/profile`, {
        method: "PUT", credentials: "include",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${accessToken}` },
        body: JSON.stringify({ minecraft_username: foundName }),
      });
      const data = await res.json();
      if (!res.ok) { setMcSt({ loading: false, saved: false, error: data.errors?.[0]?.msg || data.error || "Failed." }); return; }
      await refreshProfile();
      setMcName(foundName);
      setSearchSt("idle");
      setMcSt({ loading: false, saved: true, error: "" });
      setTimeout(() => setMcSt(s => ({ ...s, saved: false })), 3000);
    } catch { setMcSt({ loading: false, saved: false, error: "Network error." }); }
  };

  const discordLinked = !!(authUser?.discord_id || authUser?.discord_linked || authUser?.provider === "discord");

  return (
    <>
      {/* Minecraft */}
      <div style={{ background: "rgba(74,222,128,.04)", border: "1px solid rgba(74,222,128,.14)", borderRadius: 8, padding: "18px", marginBottom: 14 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
          <div style={{ width: 32, height: 32, borderRadius: 7, background: "rgba(74,222,128,.12)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <Sword style={{ width: 15, height: 15, color: "#4ade80" }} />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontFamily: "'Cinzel',serif", fontSize: 13, color: "#e2d9f3", fontWeight: 600 }}>Minecraft</div>
            <div style={{ fontSize: 11, color: "#6b6080" }}>Link your in-game username</div>
          </div>
          {authUser?.minecraft_username && (
            <span style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 11, color: "#4ade80" }}>
              <Check style={{ width: 12, height: 12 }} /> Linked
            </span>
          )}
        </div>
        <div style={{ display: "flex", gap: 8, marginBottom: 4 }}>
          <FieldWrap label="Minecraft Username" style={{ flex: 1, marginBottom: 0 }}>
            <span style={ICON_STYLE}><User style={{ width: 14, height: 14 }} /></span>
            <input type="text" value={mcName}
              onChange={e => { setMcName(e.target.value); setSearchSt("idle"); setFoundName(null); }}
              onKeyDown={e => e.key === "Enter" && searchPlayer()}
              placeholder="e.g. Steve" className="no-right-pad"
              style={INPUT_BASE} onFocus={fo} onBlur={fob} />
          </FieldWrap>
          <button onClick={searchPlayer} disabled={searchSt === "searching" || mcName.trim().length < 3}
            style={{ marginTop: 22, padding: "10px 14px", background: "linear-gradient(135deg,#7b5ea7,#5a3d8a)", border: "none", color: "#fff", fontFamily: "'Cinzel',serif", fontSize: 11, fontWeight: 700, cursor: searchSt === "searching" ? "not-allowed" : "pointer", opacity: mcName.trim().length < 3 ? .4 : 1, flexShrink: 0, height: 40 }}>
            {searchSt === "searching" ? "..." : "Find"}
          </button>
        </div>
        {searchSt === "notfound" && (
          <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 12px", background: "rgba(239,68,68,.06)", border: "1px solid rgba(239,68,68,.2)", borderRadius: 6, marginBottom: 10, fontSize: 11, color: "#ef4444", fontFamily: "'Cinzel',serif" }}>
            <AlertCircle style={{ width: 12, height: 12, flexShrink: 0 }} />
            Player not found. You must join the server first.
          </div>
        )}
        {searchSt === "found" && foundName && (
          <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", background: "rgba(74,222,128,.06)", border: "1px solid rgba(74,222,128,.25)", borderRadius: 6, marginBottom: 10 }}>
            <img src={`https://mc-heads.net/avatar/${encodeURIComponent(foundName)}/32`} alt={foundName} style={{ width: 32, height: 32, imageRendering: "pixelated" }} onError={e => { e.target.style.display = "none"; }} />
            <div style={{ flex: 1 }}>
              <div style={{ fontFamily: "'Cinzel',serif", fontSize: 12, color: "#e2d9f3", fontWeight: 700 }}>{foundName}</div>
              <div style={{ fontSize: 10, color: "#4ade80" }}>Found on the server</div>
            </div>
            <Check style={{ width: 14, height: 14, color: "#4ade80" }} />
          </div>
        )}
        <SaveBtn onClick={saveMc} loading={mcSt.loading} label="Link Minecraft"
          disabled={searchSt !== "found"} />
        <Feedback saved={mcSt.saved} error={mcSt.error} />
      </div>

      {/* Discord */}
      <div style={{ background: "rgba(88,101,242,.05)", border: "1px solid rgba(88,101,242,.2)", borderRadius: 8, padding: "18px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
          <div style={{ width: 32, height: 32, borderRadius: 7, background: "rgba(88,101,242,.15)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            {DISCORD_ICON}
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontFamily: "'Cinzel',serif", fontSize: 13, color: "#e2d9f3", fontWeight: 600 }}>Discord</div>
            <div style={{ fontSize: 11, color: "#6b6080" }}>
              {discordLinked ? "Discord account is connected" : "Connect your Discord account"}
            </div>
          </div>
          {discordLinked && (
            <span style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 11, color: "#4ade80", flexShrink: 0 }}>
              <Check style={{ width: 12, height: 12 }} /> Linked
            </span>
          )}
        </div>

        {discordLinked ? (
          <div style={{
            background: "rgba(88,101,242,.08)", border: "1px solid rgba(88,101,242,.2)",
            borderRadius: 7, padding: "10px 14px",
            fontFamily: "'Cinzel',serif", fontSize: 12, color: "#9b8dbe", textAlign: "center",
          }}>
            Discord account connected ✓
          </div>
        ) : (
          <button type="button"
            onClick={() => { window.location.href = `${API}/auth/discord?link=true`; }}
            style={{
              width: "100%", padding: "11px", borderRadius: 8,
              background: "#5865F2", border: "none",
              color: "#fff", fontFamily: "'Cinzel',serif", fontSize: 12, fontWeight: 700,
              letterSpacing: 1, textTransform: "uppercase", cursor: "pointer",
              display: "flex", alignItems: "center", justifyContent: "center", gap: 9,
              transition: ".15s",
            }}
            onMouseOver={e => { e.currentTarget.style.background = "#4752c4"; e.currentTarget.style.transform = "translateY(-1px)"; }}
            onMouseOut={e =>  { e.currentTarget.style.background = "#5865F2"; e.currentTarget.style.transform = ""; }}
          >
            {DISCORD_ICON}
            Link with Discord
            <ExternalLink style={{ width: 12, height: 12, opacity: .7 }} />
          </button>
        )}
      </div>
    </>
  );
}


/* ── Orders tab ───────────────────────────────────────────────────────────── */
function formatMoney(amount, currency = "GBP") {
  const n = Number(amount);
  if (!Number.isFinite(n)) return "";
  try {
    return new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency: currency || "GBP",
    }).format(n);
  } catch {
    return `${n.toFixed(2)} ${currency || "GBP"}`;
  }
}

function prettyStatus(status = "") {
  return String(status || "Unknown")
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, c => c.toUpperCase());
}

function statusColour(status = "") {
  const s = String(status).toUpperCase();
  if (s.includes("DELIVER") || s.includes("FULFILLED")) return "#4ade80";
  if (s.includes("SHIP")) return "#60a5fa";
  if (s.includes("PRODUCTION") || s.includes("CONFIRMED") || s.includes("PROCESS") || s === "PAID") return "#c9a84c";
  if (s.includes("CANCEL") || s.includes("REFUND")) return "#f87171";
  return "#9b8dbe";
}

function OrdersTab({ accessToken }) {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [warning, setWarning] = useState("");
  const [expanded, setExpanded] = useState(null);
  const [returnState, setReturnState] = useState({ orderId: null, loading: false, error: "", sent: false });

  const loadOrders = useCallback(async () => {
    setLoading(true);
    setError("");
    setWarning("");

    const headers = {
      Accept: "application/json",
      ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
    };

    const fetchOrders = async (url, source) => {
      const res = await fetch(url, { method: "GET", credentials: "include", headers });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `Couldn't load ${source} orders.`);
      const list = Array.isArray(data) ? data : data.orders || [];
      return list.map(order => ({ ...order, order_source: order.order_source || source }));
    };

    try {
      const [merchResult, minecraftResult] = await Promise.allSettled([
        fetchOrders(`${API}/merch/orders/me`, "merch"),
        fetchOrders(`${API}/orders/me`, "minecraft"),
      ]);

      const combined = [];
      const failures = [];

      if (merchResult.status === "fulfilled") combined.push(...merchResult.value);
      else failures.push("TOA Apparel");

      if (minecraftResult.status === "fulfilled") combined.push(...minecraftResult.value);
      else failures.push("Minecraft");

      combined.sort((a, b) => {
        const at = new Date(a.createdAt || a.created_at || 0).getTime();
        const bt = new Date(b.createdAt || b.created_at || 0).getTime();
        return bt - at;
      });

      if (!combined.length && failures.length) {
        setError(`${failures.join(" and ")} orders couldn't be loaded right now.`);
        setOrders([]);
        return;
      }

      if (failures.length) {
        setWarning(`${failures.join(" and ")} orders couldn't be loaded. The orders shown below are still available.`);
      }

      setOrders(combined);
    } catch {
      setError("Network error while loading your orders.");
      setOrders([]);
    } finally {
      setLoading(false);
    }
  }, [accessToken]);

  useEffect(() => {
    loadOrders();
  }, [loadOrders]);

  const requestReturn = async (order) => {
    const orderId = order?.id || order?.fourthwall_order_id;
    if (!orderId) return;

    setReturnState({ orderId, loading: true, error: "", sent: false });

    try {
      const res = await fetch(`${API}/merch/orders/${encodeURIComponent(orderId)}/return-request`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
        },
        body: JSON.stringify({
          reason: "Customer requested return/support from account page",
        }),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        setReturnState({
          orderId,
          loading: false,
          error: data.error || "Couldn't submit the request.",
          sent: false,
        });
        return;
      }

      setReturnState({ orderId, loading: false, error: "", sent: true });
      await loadOrders();
    } catch {
      setReturnState({
        orderId,
        loading: false,
        error: "Network error.",
        sent: false,
      });
    }
  };

  if (loading) {
    return (
      <div style={{
        minHeight: 180, display: "flex", alignItems: "center", justifyContent: "center",
        color: "#6b6080", fontFamily: "'Cinzel',serif", fontSize: 12,
      }}>
        Loading orders…
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        padding: 16, borderRadius: 8,
        background: "rgba(239,68,68,.06)", border: "1px solid rgba(239,68,68,.2)",
        color: "#f87171", fontSize: 12, fontFamily: "'Cinzel',serif", lineHeight: 1.6,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 10 }}>
          <AlertCircle style={{ width: 14, height: 14 }} />
          {error}
        </div>
        <button onClick={loadOrders} style={{
          padding: "8px 12px", borderRadius: 6, cursor: "pointer",
          border: "1px solid rgba(123,94,167,.35)", background: "rgba(123,94,167,.1)",
          color: "#c9a84c", fontFamily: "'Cinzel',serif", fontSize: 11,
        }}>
          Try Again
        </button>
      </div>
    );
  }

  if (!orders.length) {
    return (
      <div style={{
        minHeight: 210, display: "flex", flexDirection: "column",
        alignItems: "center", justifyContent: "center", textAlign: "center",
        border: "1px solid rgba(123,94,167,.18)", borderRadius: 8,
        background: "rgba(123,94,167,.045)", padding: 24,
      }}>
        <Package style={{ width: 30, height: 30, color: "#5a4b72", marginBottom: 12 }} />
        <div style={{
          fontFamily: "'Cinzel',serif", color: "#e2d9f3",
          fontWeight: 700, fontSize: 13, marginBottom: 6,
        }}>
          No orders yet
        </div>
        <div style={{ color: "#6b6080", fontSize: 11, lineHeight: 1.6 }}>
          TOA Apparel and Minecraft purchases made while signed in will appear here.
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {warning && (
        <div style={{
          padding: "10px 12px", borderRadius: 7,
          background: "rgba(201,168,76,.06)", border: "1px solid rgba(201,168,76,.18)",
          color: "#c9a84c", fontSize: 10, lineHeight: 1.6,
        }}>{warning}</div>
      )}
      {orders.map(order => {
        const source = order.order_source || "merch";
        const isMinecraftOrder = source === "minecraft";
        const id = order.id || order.fourthwall_order_id;
        const friendlyId = order.order_number || order.friendlyId || order.friendly_id || order.fourthwall_friendly_id || id;
        const status = order.status || "CONFIRMED";
        const items = order.items || [];
        const primaryItem = items[0] || null;
        const primaryItemName = primaryItem && (
          primaryItem.product_name ||
          primaryItem.productName ||
          primaryItem.name ||
          primaryItem.product?.name ||
          primaryItem.variant?.product?.name
        );
        const primaryItemImage = primaryItem && (
          primaryItem.image ||
          primaryItem.imageUrl ||
          primaryItem.image_url ||
          primaryItem.variant?.images?.[0]?.url ||
          primaryItem.variant?.images?.[0]?.transformedUrl
        );
        const itemSummary = primaryItemName
          ? `${primaryItemName}${items.length > 1 ? ` + ${items.length - 1} more` : ""}`
          : (isMinecraftOrder ? "Minecraft purchase" : "TOA Apparel order");
        const total =
          order.total ??
          order.amount ??
          order.amounts?.total?.value ??
          order.total_amount;
        const currency =
          order.currency ??
          order.amounts?.total?.currency ??
          order.total_currency ??
          "GBP";
        const createdAt = order.createdAt || order.created_at;
        const trackingUrl =
          order.trackingUrl ||
          order.tracking_url ||
          order.fulfillment?.trackingUrl ||
          order.fulfillment?.tracking_url;
        const trackingNumber =
          order.trackingNumber ||
          order.tracking_number ||
          order.fulfillment?.trackingNumber ||
          order.fulfillment?.tracking_number;
        const isExpanded = expanded === id;
        const returnStatus = order.return_status || order.returnStatus;

        return (
          <div key={id} style={{
            border: "1px solid rgba(123,94,167,.2)",
            borderRadius: 8,
            background: "rgba(123,94,167,.045)",
            overflow: "hidden",
          }}>
            <button
              type="button"
              onClick={() => setExpanded(isExpanded ? null : id)}
              style={{
                width: "100%", border: "none", background: "transparent", cursor: "pointer",
                padding: "14px 15px", display: "flex", alignItems: "center", gap: 12,
                textAlign: "left", color: "inherit",
              }}
            >
              <div style={{
                width: 36, height: 36, borderRadius: 8, flexShrink: 0,
                background: "rgba(201,168,76,.1)", border: "1px solid rgba(201,168,76,.18)",
                display: "flex", alignItems: "center", justifyContent: "center",
              }}>
                {isMinecraftOrder && primaryItemImage
                  ? <img
                      src={primaryItemImage}
                      alt={primaryItemName || "Minecraft item"}
                      style={{ width: 30, height: 30, objectFit: "contain", imageRendering: "pixelated" }}
                    />
                  : isMinecraftOrder
                    ? <Sword style={{ width: 16, height: 16, color: "#c9a84c" }} />
                    : <Package style={{ width: 16, height: 16, color: "#c9a84c" }} />}
              </div>

              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{
                  fontFamily: "'Cinzel',serif", color: "#e2d9f3",
                  fontSize: 12, fontWeight: 700, marginBottom: 3,
                }}>
                  {isMinecraftOrder ? "Minecraft" : "TOA Apparel"} · Order #{friendlyId}
                </div>
                <div style={{
                  fontSize: 10.5, color: isMinecraftOrder ? "#c9a84c" : "#a99dbb",
                  fontWeight: 600, marginBottom: 3, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                }}>
                  {itemSummary}
                </div>
                <div style={{ fontSize: 10, color: "#6b6080" }}>
                  {createdAt ? new Date(createdAt).toLocaleDateString("en-GB", {
                    day: "2-digit", month: "short", year: "numeric",
                  }) : "Order"}
                  {Number.isFinite(Number(total)) ? ` · ${formatMoney(total, currency)}` : ""}
                </div>
              </div>

              <div style={{ textAlign: "right", flexShrink: 0 }}>
                <div style={{
                  fontSize: 10, fontWeight: 700, fontFamily: "'Cinzel',serif",
                  color: statusColour(status),
                }}>
                  {prettyStatus(status)}
                </div>
              </div>

              {isExpanded
                ? <ChevronUp style={{ width: 14, height: 14, color: "#6b6080" }} />
                : <ChevronDown style={{ width: 14, height: 14, color: "#6b6080" }} />}
            </button>

            {isExpanded && (
              <div style={{
                borderTop: "1px solid rgba(123,94,167,.16)",
                padding: "14px 15px 15px",
              }}>
                {items.length > 0 && (
                  <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 14 }}>
                    {items.map((item, index) => {
                      const image =
                        item.image ||
                        item.imageUrl ||
                        item.image_url ||
                        item.variant?.images?.[0]?.url ||
                        item.variant?.images?.[0]?.transformedUrl;
                      const name =
                        item.product_name ||
                        item.productName ||
                        item.name ||
                        item.product?.name ||
                        item.variant?.product?.name ||
                        (isMinecraftOrder ? "Minecraft store item" : "Merch item");
                      const colour =
                        item.colour ||
                        item.color ||
                        item.variant?.attributes?.color?.name;
                      const size =
                        item.size ||
                        item.variant?.attributes?.size?.name;
                      const qty = item.quantity || 1;

                      return (
                        <div key={item.id || item.variant_id || item.variant?.id || index} style={{
                          display: "grid", gridTemplateColumns: "48px 1fr", gap: 10,
                          alignItems: "center",
                        }}>
                          <div style={{
                            width: 48, height: 56, borderRadius: 5, overflow: "hidden",
                            border: "1px solid rgba(123,94,167,.18)",
                            background: "rgba(255,255,255,.025)",
                            display: "flex", alignItems: "center", justifyContent: "center",
                          }}>
                            {image
                              ? <img
                                  src={image}
                                  alt={name}
                                  style={{
                                    width: "100%", height: "100%",
                                    objectFit: isMinecraftOrder ? "contain" : "cover",
                                    padding: isMinecraftOrder ? 5 : 0,
                                    boxSizing: "border-box",
                                    imageRendering: isMinecraftOrder ? "pixelated" : "auto",
                                  }}
                                />
                              : isMinecraftOrder
                                ? <Sword style={{ width: 16, height: 16, color: "#4a4060" }} />
                                : <Package style={{ width: 16, height: 16, color: "#4a4060" }} />}
                          </div>

                          <div>
                            <div style={{
                              fontFamily: "'Cinzel',serif", fontSize: 11,
                              color: "#e2d9f3", fontWeight: 600,
                            }}>
                              {name}
                            </div>
                            <div style={{ marginTop: 3, fontSize: 10, color: "#6b6080" }}>
                              {[colour, size].filter(Boolean).join(" · ")}
                              {(colour || size) ? " · " : ""}Qty {qty}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {isMinecraftOrder && (
                  <div style={{
                    display: "flex", alignItems: "center", gap: 9,
                    padding: "10px 11px", marginBottom: 12,
                    background: "rgba(123,94,167,.06)", border: "1px solid rgba(123,94,167,.18)",
                    borderRadius: 7, color: "#a99dbb", fontSize: 10, lineHeight: 1.55,
                  }}>
                    <Sword style={{ width: 14, height: 14, color: "#9b7fd4", flexShrink: 0 }} />
                    <span>
                      Digital purchase{order.minecraft_username ? ` for ${order.minecraft_username}` : ""}. In-game items are fulfilled automatically after payment.
                    </span>
                  </div>
                )}

                {(trackingUrl || trackingNumber) && (
                  <div style={{
                    display: "flex", alignItems: "center", gap: 9,
                    padding: "10px 11px", marginBottom: 12,
                    background: "rgba(96,165,250,.06)", border: "1px solid rgba(96,165,250,.18)",
                    borderRadius: 7,
                  }}>
                    <Truck style={{ width: 14, height: 14, color: "#60a5fa", flexShrink: 0 }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ color: "#cbd5e1", fontSize: 10, fontWeight: 600 }}>Tracking</div>
                      {trackingNumber && (
                        <div style={{ color: "#6b7b90", fontSize: 10, marginTop: 2 }}>{trackingNumber}</div>
                      )}
                    </div>
                    {trackingUrl && (
                      <a
                        href={trackingUrl}
                        target="_blank"
                        rel="noreferrer"
                        style={{
                          color: "#60a5fa", fontSize: 10, fontFamily: "'Cinzel',serif",
                          textDecoration: "none", display: "flex", alignItems: "center", gap: 4,
                        }}
                      >
                        Track <ExternalLink style={{ width: 10, height: 10 }} />
                      </a>
                    )}
                  </div>
                )}

                {!isMinecraftOrder && returnStatus && (
                  <div style={{
                    marginBottom: 10, padding: "9px 10px", borderRadius: 6,
                    background: "rgba(201,168,76,.06)", border: "1px solid rgba(201,168,76,.16)",
                    color: "#c9a84c", fontSize: 10, fontFamily: "'Cinzel',serif",
                  }}>
                    Return request: {prettyStatus(returnStatus)}
                  </div>
                )}

                {!isMinecraftOrder && (
                  <>
                    <button
                      type="button"
                      onClick={() => requestReturn(order)}
                      disabled={returnState.loading && returnState.orderId === id}
                      style={{
                        width: "100%", padding: "9px 11px", borderRadius: 7, cursor: "pointer",
                        border: "1px solid rgba(123,94,167,.3)", background: "rgba(123,94,167,.08)",
                        color: "#c4b5d8", fontFamily: "'Cinzel',serif", fontSize: 10, fontWeight: 700,
                        display: "flex", alignItems: "center", justifyContent: "center", gap: 7,
                        opacity: returnState.loading && returnState.orderId === id ? .6 : 1,
                      }}
                    >
                      <RotateCcw style={{ width: 12, height: 12 }} />
                      {returnState.loading && returnState.orderId === id
                        ? "Sending…"
                        : "Request Return / Order Help"}
                    </button>

                    {returnState.orderId === id && returnState.sent && (
                      <div style={{ marginTop: 8, fontSize: 10, color: "#4ade80", textAlign: "center" }}>
                        Request sent successfully.
                      </div>
                    )}

                    {returnState.orderId === id && returnState.error && (
                      <div style={{ marginTop: 8, fontSize: 10, color: "#f87171", textAlign: "center" }}>
                        {returnState.error}
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}


/* ── Root modal ───────────────────────────────────────────────────────────── */
export default function SettingsModal({ onClose }) {
  const { authUser, accessToken, doRefresh } = useAuth();
  const [tab, setTab] = useState("profile");

  return (
    <OriginalModalShell onClose={onClose} maxWidth={520}>
      <div style={{ padding: "24px 32px 28px", position: "relative" }}>

        {/* Close — matches AuthModal exactly */}
        <button onClick={onClose} style={{
          position: "absolute", top: 16, right: 20,
          width: 28, height: 28, background: "rgba(255,255,255,.04)",
          border: "1px solid #2a2040", cursor: "pointer",
          color: "#4a4060", display: "flex", alignItems: "center", justifyContent: "center", transition: ".15s",
        }}
          onMouseOver={e => { e.currentTarget.style.borderColor = "#7b5ea7"; e.currentTarget.style.color = "#c9a84c"; }}
          onMouseOut={e =>  { e.currentTarget.style.borderColor = "#2a2040"; e.currentTarget.style.color = "#4a4060"; }}
        >
          <X style={{ width: 11, height: 11 }} />
        </button>

        {/* Header — matches AuthModal exactly */}
        <div style={{ textAlign: "center", marginBottom: 22 }}>
          <div style={{ fontFamily: "'Cinzel Decorative',serif", fontSize: 13, color: "#c9a84c", marginBottom: 4 }}>The Ones Above</div>
          <div style={{ fontFamily: "'Cinzel',serif", fontSize: 22, fontWeight: 700, color: "#e2d9f3", marginBottom: 4 }}>Account Settings</div>
          <div style={{ fontSize: 12, color: "#6b6080", fontFamily: "'Cinzel',serif" }}>Manage your profile, connections and orders</div>
        </div>

        {/* Tab switcher — matches AuthModal exactly */}
        <div style={{ display: "flex", border: "1px solid #2a2040", borderRadius: 8, overflow: "hidden", marginBottom: 22 }}>
          {[
            { id: "profile", label: "Profile" },
            { id: "connections", label: "Connections" },
            { id: "orders", label: "Orders" },
          ].map(t => (
            <button key={t.id} onClick={() => setTab(t.id)} style={{
              flex: 1, padding: "9px", fontFamily: "'Cinzel',serif", fontSize: 11, fontWeight: 700,
              letterSpacing: 1, textTransform: "uppercase", cursor: "pointer", border: "none",
              background: tab === t.id ? "linear-gradient(135deg,#7b5ea7,#5a3d8a)" : "transparent",
              color: tab === t.id ? "#fff" : "#6b6080", transition: ".15s",
            }}>{t.label}</button>
          ))}
        </div>

        {tab === "profile"     && <ProfileTab authUser={authUser} accessToken={accessToken} doRefresh={doRefresh} />}
        {tab === "connections" && <ConnectionsTab authUser={authUser} accessToken={accessToken} />}
        {tab === "orders"      && <OrdersTab accessToken={accessToken} />}
      </div>
    </OriginalModalShell>
  );
}